import { Dropdown, Label } from '@/components';
import InputError from '@/components/InputError';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { SelectValue } from '@/types';

interface ResponsiveDropdownProps {
  label?: string;
  options: SelectValue[];
  selected: SelectValue | null;
  onChange: (option: SelectValue) => void;
  onBlur?: () => void;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string; // Mantén className para estilos adicionales si son necesarios
}

const FaqDropdown = ({
  label,
  options,
  selected,
  onChange,
  onBlur,
  error,
  placeholder,
  disabled,
  className,
  ...rest
}: ResponsiveDropdownProps) => {
  const { isMobile } = useIsDesktop();

  // Clases base para el select
  const baseSelectClasses = `
    block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm
    focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
    sm:text-sm
    appearance-none
    relative
  `;

  // Clases para el estado hover
  const hoverSelectClasses = `
    hover:border-blue-400
  `;

  if (isMobile) {
    // Determinar el valor actual del select.
    // Si no hay `selected` (es null), el valor debe ser una cadena vacía para que se muestre el placeholder.
    // Si `selected` tiene un valor, se usa ese valor.
    const currentValue = selected ? selected.value : '';
    const placeholderTextClasses = !selected
      ? 'text-gray-500 text-sm'
      : 'text-gray-900 text-sm';

    return (
      <fieldset className="space-y-1">
        {label && <Label>{label}</Label>}
        <div className="relative">
          <select
            className={`${baseSelectClasses} ${hoverSelectClasses} ${placeholderTextClasses} ${
              className || ''
            }`}
            disabled={disabled}
            value={currentValue} // Usa el valor calculado aquí
            onBlur={onBlur}
            onChange={(e) => {
              const value = e.target.value;
              // Si el valor seleccionado es el del placeholder (cadena vacía),
              // se llama a onChange con null para indicar que no hay selección.
              if (value === '') {
                onChange(null as any);
                return;
              }
              const selectedOption = options.find(
                // Convertir option.value a string para una comparación más segura,
                // ya que e.target.value siempre es un string.
                (option) => String(option.value) === value
              );
              if (selectedOption) {
                onChange(selectedOption);
              }
            }}
          >
            {/* Renderiza la opción del placeholder solo si se provee uno */}
            {placeholder && (
              <option disabled value="">
                {placeholder}
              </option>
            )}
            {/* Renderiza las opciones regulares */}
            {options.map((option) => (
              <option
                key={option.value}
                disabled={option.disabled}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {error && <InputError.Label>{error}</InputError.Label>}
      </fieldset>
    );
  } else {
    return (
      <Dropdown
        {...{
          label,
          options,
          selected,
          onChange,
          onBlur,
          error,
          placeholder,
          disabled,
          className,
          ...rest,
        }}
      />
    );
  }
};

export default FaqDropdown;
